import Navbar from "../components/Navbar";
import { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import axios from "../api/axios";
import CheckoutForm from "../components/CheckoutForm";
import Footer from "../components/Footer";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
);

const MNF = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const packages = useMemo(
    () => [
      {
        id: 1,
        name: "Player Highlights",
        type: "player",
        price: 15,
        description:
          "Perfect for individual players wanting a sharp, professional highlight reel.",
        features: [
          "Goals, assists, skills and key moments",
          "Clean cuts and smooth pacing",
          "Background music included",
        ],
      },
    ],
    []
  );

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [contactDetails, setContactDetails] = useState({
  name: "",
  email: "",
  phone: "",
});
  const [loadingContact, setLoadingContact] = useState(false);

  // index of current section starting from 0
  const [currentSection, setCurrentSection] = useState(0);

  const sections = [
    "Selection", "Details", "Payment"
  ]

  const progressPercentage =
  sections.length > 1 ? (currentSection / (sections.length - 1)) * 100 : 0;

  function packageSelection(pkg) {
    setSelectedPackage(pkg);
    setCurrentSection(() => currentSection + 1)
  }

  
  const initiatePayment = async () => {
    if (!selectedPackage) return;

    try {
      setLoadingPayment(true);
      setError("");
      setClientSecret(null);

      const response = await axios.post("/api/create-intent", {
        paymentMethodType: "card",
        currency: "gbp",
        amount: selectedPackage.price * 100,
        packageName: selectedPackage.name,
        type: selectedPackage.type,
      });

      if (response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
        setSessionId(response.data.sessionId);
      } else {
        throw new Error("Could not retrieve client secret from server");
      }
    } catch (err) {
      console.error("Error creating payment intent:", err);
      setError("Failed to initialize payment.");
    } finally {
      setLoadingPayment(false);
    }
  };

  const sendBookingEmail = async () => {
  if (!selectedPackage) return;

  const templateParams = {
    customer_name: contactDetails.name,
    customer_email: contactDetails.email,
    customer_phone: contactDetails.phone,
    package_name: `MNF ${selectedPackage.name}`,
    package_type: selectedPackage.type,
    package_price: selectedPackage.price,
  };

  await emailjs.send(
    process.env.REACT_APP_EMAILJS_SERVICE_ID,
    process.env.REACT_APP_BOOKING_TEMPLATE_ID,
    templateParams,
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY
  );
};

async function handleContactSubmit(e) {
  e.preventDefault();

  if (!contactDetails.name || !contactDetails.email || !contactDetails.phone) {
    setError("Please fill in all contact details.");
    return;
  }

  if (!isValidUKPhoneNumber(contactDetails.phone)) {
    setError("Please enter a valid UK phone number.");
    return;
  }

  try {
    setError("");

    setLoadingContact(true);
    await sendBookingEmail();

    setCurrentSection(2);
    initiatePayment();
  } catch (err) {
    console.error("EmailJS error:", err);
    setError("Could not send your contact details. Please try again.");
  } finally {
    setLoadingContact(false);
  }
}

function isValidUKPhoneNumber(phone) {
  const cleanedPhone = phone.replace(/\s+/g, "");

  const ukPhoneRegex = /^(?:\+44|0)7\d{9}$/;

  return ukPhoneRegex.test(cleanedPhone);
}

function scrollToBookingSection() {
              document
                .getElementById("booking-section")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }


return (
  <>
  {/* <div className="aspect-video"> 
     <iframe
      className="h-full w-full"
      src="https://www.youtube.com/embed/iRQB8zr2o0g?modestbranding=1&rel=0"
      title="YouTube video player"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    /> 
   </div> */}
      <Navbar buttonAction={scrollToBookingSection}/>

      <section className="max-w-[1200px] w-full mx-auto mb-16 px-4">
<div className="relative mx-auto overflow-hidden rounded-2xl border border-blue-200 shadow-[0_12px_35px_rgba(37,99,235,0.10)] bg-white">
  
  {/* Loader */}
  {!videoLoaded && (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-white">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
    </div>
  )}

  {/* Video wrapper (preserves aspect ratio) */}
  <div className="h-[200px] w-full overflow-hidden sm:h-[330px] md:h-auto">
    <video
      src="/mnf_trailer.mp4"
      autoPlay
      muted
      loop
      playsInline
      onLoadedData={() => setVideoLoaded(true)}
      className={`h-full w-full object-cover transition-opacity duration-500 md:h-auto md:object-contain ${
        videoLoaded ? "opacity-100" : "opacity-0"
      }`}
    />
  </div>

  <AnimatePresence>
    {videoLoaded && (
      <>
        {/* Top door */}
        <motion.div
          initial={{ y: "0%" }}
          animate={{ y: "-100%" }}
          transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
          className="absolute left-0 top-0 z-30 h-1/2 w-full bg-white"
        >
          <div className="absolute bottom-0 h-2 w-full bg-gradient-to-r from-slate-900 via-blue-600 to-slate-900" />
        </motion.div>

        {/* Bottom door */}
        <motion.div
          initial={{ y: "0%" }}
          animate={{ y: "100%" }}
          transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
          className="absolute bottom-0 left-0 z-30 h-1/2 w-full bg-white"
        >
          <div className="absolute top-0 h-2 w-full bg-gradient-to-r from-slate-900 via-blue-600 to-slate-900" />
        </motion.div>

        {/* Content overlay */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/40 px-4 text-center"
        >
          <img src="/images/MNF.png" className="h-[50px] md:h-[100px]" alt="mnf"/>
          <h2 className="text-3xl font-extrabold tracking-wide text-white drop-shadow-lg md:text-6xl">
            Monday Night Football
          </h2>

          <div className="mt-4 h-[3px] w-16 rounded-full bg-blue-500" />

          <h3 className="md:mt-4 text-xl font-semibold tracking-widest text-blue-400 drop-shadow md:text-3xl">
            Every Monday
          </h3>

          <p className="hidden md:block mt-4 max-w-xl text-sm text-slate-200 md:text-lg">
            Forest Recreation Ground.
          </p>
        </motion.div>
      </>
    )}
  </AnimatePresence>
</div>
        <div className="mt-2 flex flex-col items-center text-center">
          {/* <p className="text-sm font-semibold text-slate-600">
            Choose your highlight package below
          </p> */}

          <button
            type="button"
            onClick={scrollToBookingSection}
            className="mt-3 flex h-12 w-12 animate-bounce items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-slate-900"
            aria-label="Scroll to booking section"
          >
            ↓
          </button>
        </div>
        <div className="mt-10 text-center max-w-3xl mx-auto md:block hidden">
          <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-4 py-1 text-sm font-semibold">
            Fast turnaround • Secure payment • Professional editing
          </span>

          <h1 className="mt-5 text-3xl md:text-5xl font-bold text-slate-900 leading-tight">
            Turn Match Footage Into
            <span className="text-blue-600"> Share-Worthy Highlights</span>
          </h1>

          <p className="mt-4 text-slate-600 text-lg max-w-2xl mx-auto">
            Choose your package below and book instantly. Once payment is completed,
            your highlight edit can be processed without delay.
          </p>
        </div>

<div id="booking-section" className="mt-3 md:mt-12 grid grid-cols-1 gap-8 items-start scroll-mt-8">          <div>
            <div className="mb-5 text-center md:text-normal">
              <h2 className="text-2xl font-bold text-slate-900">
                {{
                    0: "Choose Your Package",
                    1: "Enter Contact Details",
                    2: "Checkout"
                }[currentSection]}
              </h2>
              <p className="mt-2 text-slate-600">
                {{
                    0: "Pick the option that best fits your footage and complete checkout today.",
                    1: "Let us know who we're talking to so we can keep in touch.",
                    2: "Review your order and confirm payment."
                }[currentSection]}
              </p>
            </div>
          <section className="relative flex h-8 items-center px-0 mb-5">
            <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-slate-200" />
            <div
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-slate-900 transition-all duration-300"
              style={{ width: `calc(${progressPercentage}%` }}
              // style={{ width: `calc(${progressPercentage}% - 2.5rem * ${progressPercentage / 100})` }}

            />
            <div className="relative z-10 flex w-full justify-between">
              {sections.map((section, index) => (
                <div
                  key={section}
                  className={`h-3 w-3 rounded-full transition ${
                    index <= currentSection ? 'bg-slate-900' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </section>
              {
                currentSection === 0 && 
            <div className="CLASSpackageSelection grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map((pkg) => {
                const isSelected = selectedPackage?.id === pkg.id;
                const isRecommended = pkg.id === 2;

                return (
                  <div
                    key={pkg.id}
                    onClick={() => packageSelection(pkg)}
                    className={`relative cursor-pointer rounded-2xl border p-6 transition-all duration-200 ${
                      isSelected
                        ? "border-blue-600 bg-blue-50 shadow-[0_10px_30px_rgba(37,99,235,0.12)] scale-[1.02]"
                        : "border-slate-200 bg-white shadow-sm hover:border-blue-300 hover:shadow-md"
                    }`}
                  >
                    {isRecommended && (
                      <span className="absolute -top-3 right-4 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1 shadow">
                        Most Popular
                      </span>
                    )}

                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="inline-block text-xs uppercase tracking-wide bg-slate-100 text-slate-700 px-2 py-1 rounded-full mb-3">
                          {pkg.type}
                        </span>
                        <h3 className="text-2xl font-bold text-slate-900">
                          {pkg.name}
                        </h3>
                      </div>

                      {isSelected && (
                        <span className="text-xs font-semibold bg-blue-600 text-white px-2 py-1 rounded-full">
                          Selected
                        </span>
                      )}
                    </div>

                    <p className="mt-4 text-4xl font-bold text-blue-600">
                      £{pkg.price}
                    </p>

                    <p className="mt-3 text-sm text-slate-600 md:block hidden">{pkg.description}</p>

                    <ul className="mt-5 space-y-3 text-sm text-slate-700">
                      {pkg.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-blue-600 mt-[2px]">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      className={`mt-6 w-full rounded-xl py-3 font-semibold transition ${
                        isSelected
                          ? "bg-blue-600 text-white"
                          : "bg-slate-900 text-white hover:bg-blue-600"
                      }`}
                    >
                      {isSelected ? "Package Selected" : "Choose This Package"}
                    </button>
                  </div>
                );
              })}
            </div>

              }
            {currentSection === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

                {/* Package Summary */}
                {selectedPackage && (
                  <div className="rounded-2xl border border-blue-200 bg-white p-6 md:sticky md:top-24 md:block hidden">
                    <span className="inline-flex rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-semibold">
                      Selected Package
                    </span>
                    <h3 className="mt-4 text-2xl font-bold text-slate-900">
                      {selectedPackage.name}
                    </h3>
                    <div className="hidden md:block">

                    <p className="mt-2 text-slate-600 capitalize">
                      {selectedPackage.type} package
                    </p>

                    <p className="mt-4 text-4xl font-bold text-blue-600">
                      £{selectedPackage.price}
                    </p>

                    </div>

                    <ul className="mt-5 space-y-3 text-sm text-slate-700">
                      {selectedPackage.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-blue-600 mt-[2px]">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                      Complete your details to continue securely to checkout.
                    </div>
                  </div>
                )}

                                {/* Contact Form */}
                <form
                  onSubmit={handleContactSubmit}
                  id="contact-form"
                  className="rounded-2xl border border-blue-200 bg-white p-6  shadow-[0_12px_35px_rgba(37,99,235,0.10)]"
                >
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={contactDetails.name}
                        onChange={(e) =>
                          setContactDetails({
                            ...contactDetails,
                            name: e.target.value,
                          })
                        }
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={contactDetails.email}
                        onChange={(e) =>
                          setContactDetails({
                            ...contactDetails,
                            email: e.target.value,
                          })
                        }
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                        placeholder="you@example.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={contactDetails.phone}
                        onChange={(e) =>
                          setContactDetails({
                            ...contactDetails,
                            phone: e.target.value,
                          })
                        }
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                        placeholder="07123 456789"
                        required
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="mt-4 text-sm text-red-600">{error}</p>
                  )}

                  <div className="mt-6 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrentSection(0)}
                      className="w-full rounded-xl border border-slate-300 py-3 font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Back
                    </button>

                    <button
                      type="submit"
                      disabled={loadingContact}
                      className={`w-full rounded-xl py-3 font-semibold text-white transition ${
                        loadingContact
                          ? "bg-slate-400 cursor-not-allowed"
                          : "bg-slate-900 hover:bg-blue-600"
                      }`}
                    >
                      {loadingContact ? "Submitting..." : "Continue"}
                    </button>
                  </div>
                </form>
              </div>
            )}
              { currentSection === 2 &&
            <div className="CLASSpayment lg:sticky lg:top-24">
              <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-[0_12px_35px_rgba(37,99,235,0.10)]">
                <div className="border-b border-slate-200 pb-5">
                  <span className="inline-flex rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-semibold">
                    Secure Checkout
                  </span>

                  <h2 className="mt-4 text-2xl font-bold text-slate-900">
                    Complete Your Booking
                  </h2>

                  <p className="mt-2 text-slate-600">
                    Your selected package is ready. Pay now to secure your highlight edit.
                  </p>
                </div>

                {selectedPackage && (
                  <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-lg font-semibold text-slate-900">
                          {selectedPackage.name}
                        </p>
                        <p className="text-slate-600 capitalize">
                          {selectedPackage.type} package
                        </p>
                      </div>

                      <div className="text-3xl font-bold text-blue-600">
                        £{selectedPackage.price}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600">✓</span>
                        <span>Secure Stripe checkout</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600">✓</span>
                        <span>Quick and simple booking process</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600">✓</span>
                        <span>Professional edit tailored to your footage</span>
                      </div>
                    </div>
                  </div>
                )}

                {error && <div className="mt-4 text-red-600">{error}</div>}

                {loadingPayment && (
                  <div className="mt-5 rounded-xl bg-blue-50 border border-blue-200 p-4 text-blue-700 font-medium">
                    Preparing secure checkout...
                  </div>
                )}

                {clientSecret && selectedPackage && (
                  <div className="mt-6">
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <CheckoutForm 
                        selectedPackage={selectedPackage} 
                        sessionId={sessionId}
                      />
                    </Elements>
                  </div>
                )}

                <p className="mt-5 text-xs text-slate-500 text-center">
                  Secure payment powered by Stripe
                </p>
              </div>
            </div>

              }
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default MNF;